import React from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  PaidOutlined as CostIcon,
  LayersOutlined as LayerIcon,
  CalculateOutlined as ValuationIcon,
  TravelExploreOutlined as SourceIcon,
  FactCheckOutlined as BasisIcon,
  PlayCircleOutline as ActivationIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';

// ── Sub-components (mirrors InventoryModuleHelpModal / LotsHelpModal pattern) ──

interface SectionProps {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ icon, title, children }) => {
  const theme = useTheme();
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Box sx={{ color: theme.palette.primary.main, display: 'flex', fontSize: 18 }}>
          {icon}
        </Box>
        <Typography variant="subtitle1" fontWeight={700} sx={{ letterSpacing: '-0.01em' }}>
          {title}
        </Typography>
      </Box>
      {children}
    </Box>
  );
};

interface FieldRowProps {
  label: string;
  children: React.ReactNode;
  accent?: boolean;
}

const FieldRow: React.FC<FieldRowProps> = ({ label, children, accent }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        py: 0.75,
        px: 1.25,
        borderRadius: 1.5,
        '&:hover': { backgroundColor: theme.palette.action.hover },
      }}
    >
      <Typography
        variant="body2"
        fontWeight={600}
        sx={{
          minWidth: 150,
          flexShrink: 0,
          color: accent ? theme.palette.primary.main : theme.palette.text.primary,
        }}
      >
        {label}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
        {children}
      </Typography>
    </Box>
  );
};

interface NoteProps {
  children: React.ReactNode;
  variant?: 'info' | 'warning';
}

const Note: React.FC<NoteProps> = ({ children, variant = 'info' }) => {
  const theme = useTheme();
  const isWarning = variant === 'warning';
  return (
    <Box
      sx={{
        mt: 1.25,
        px: 1.75,
        py: 1.25,
        borderRadius: 2,
        backgroundColor: isWarning
          ? (theme.palette.mode === 'dark' ? 'rgba(250,173,20,0.1)' : '#fffbeb')
          : (theme.palette.mode === 'dark' ? 'rgba(9,105,218,0.1)' : '#f0f7ff'),
        borderLeft: `3px solid ${isWarning ? '#d97706' : theme.palette.primary.main}`,
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
        {children}
      </Typography>
    </Box>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * COST-GUIDE.1 — the single "How cost tracking works" reference, opened from
 * every cost surface (item Cost section, Inventory settings, Stock Value page,
 * Movements report). Amounts in the worked example are deliberately
 * currency-neutral: this is static copy shared by venues on different
 * currencies, so it renders no money and formats nothing.
 */
const CostTrackingHelpModal: React.FC<Props> = ({ open, onClose }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(6px)',
            backgroundColor: isDark ? 'rgba(0,0,0,0.55)' : 'rgba(30,30,50,0.35)',
          },
        },
      }}
      PaperProps={{
        sx: {
          background: isDark
            ? 'rgba(22, 22, 35, 0.90)'
            : 'rgba(255, 255, 255, 0.92)',
          backdropFilter: 'blur(32px)',
          WebkitBackdropFilter: 'blur(32px)',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'}`,
          boxShadow: isDark
            ? '0 32px 80px rgba(0,0,0,0.6)'
            : '0 32px 80px rgba(0,0,0,0.16)',
          borderRadius: 3,
          maxHeight: '88vh',
        },
      }}
    >
      {/* ── Sticky header ──────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: isDark ? 'rgba(22,22,35,0.95)' : 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderRadius: '12px 12px 0 0',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CostIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2, letterSpacing: '-0.02em' }}>
              Cost Tracking — How It Works
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Where every cost number comes from
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: theme.palette.text.secondary,
            '&:hover': { color: theme.palette.text.primary, backgroundColor: theme.palette.action.hover },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* ── Scrollable body ─────────────────────────────────────────────────── */}
      <DialogContent sx={{ px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 3.5 }}>

        {/* Intro */}
        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
          Cost tracking follows the money in your stock. Every time you receive, transfer, or use
          stock, the system records what that specific batch actually cost — so your reports show
          what you really paid, not a current estimate.
        </Typography>

        <Divider />

        {/* ── Core Concepts ──────────────────────────────────────────────────── */}
        <Section icon={<LayerIcon fontSize="inherit" />} title="Core Concepts">
          <Box sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${theme.palette.divider}` }}>
            <FieldRow label="Cost layer" accent>
              A batch of stock you received at a particular price. Receive 6 bottles at 10, then 6
              more at 14, and you have two layers. Each keeps its own cost for as long as any of it
              remains.
            </FieldRow>
            <FieldRow label="Oldest used first">
              As stock goes out, it&apos;s drawn from the oldest layer first, at the price you actually
              paid for it (first in, first out). The layer marked <strong>Next</strong> in the
              item&apos;s Cost section is the one the next sale or transfer will draw from.
            </FieldRow>
            <FieldRow label="Value on hand" accent>
              The sum of each remaining batch at its own cost. It is <strong>not</strong> an average,
              and <strong>not</strong> the latest price applied to everything.
            </FieldRow>
            <FieldRow label="Basis">
              Whether a cost was actually recorded (<strong>recorded</strong>) or worked out from the
              best available figure (<strong>estimated</strong>). Estimated costs carry a coloured
              dot — hover it to see why.
            </FieldRow>
            <FieldRow label="Uncosted">
              Stock that arrived with no price recorded at all. It&apos;s real stock and shows in your
              quantities, but it&apos;s excluded from value totals until a cost is recorded.
            </FieldRow>
          </Box>
        </Section>

        <Divider />

        {/* ── How stock is valued ────────────────────────────────────────────── */}
        <Section icon={<ValuationIcon fontSize="inherit" />} title="How stock is valued">
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            Say you hold two bottles of the same wine: one from a batch that cost 10, one from a
            batch that cost 14. Your value on hand is <strong>24</strong> — each bottle at what that
            batch actually cost.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, mt: 1.25 }}>
            It is not 2 × 14 (the latest price), and not 2 × 12 (an average). If you sell one bottle,
            the one that cost 10 goes first, leaving 14 on hand.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, mt: 1.25 }}>
            <strong>You can always see the breakdown.</strong> Open the item and look at the{' '}
            <strong>Cost</strong> section: every remaining batch is listed with its own quantity and
            unit cost, oldest first, and the totals are simply those lines added up.
          </Typography>
          <Note>
            <strong>Why it works this way.</strong> Valuing each batch at what it actually cost means
            your stock value reflects real money spent. An average would hide it, and using the
            latest price would silently revalue stock you bought cheaper.
          </Note>
        </Section>

        <Divider />

        {/* ── Where each number comes from ───────────────────────────────────── */}
        <Section icon={<SourceIcon fontSize="inherit" />} title="Where each number comes from">
          <Box sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${theme.palette.divider}` }}>
            <FieldRow label="Item → Cost section" accent>
              The live batches for one item: each remaining layer with its quantity, unit cost, and
              value, oldest first. This is the detail behind every other figure.
            </FieldRow>
            <FieldRow label="Stock Value page">
              The same calculation across the whole venue — every remaining batch at its own cost,
              added up. Accounting-grade once cost tracking is active.
            </FieldRow>
            <FieldRow label="Movements report">
              <strong>Closing Value</strong> is the closing position valued the same way — the sum of
              the remaining batches, not closing quantity × one price.
            </FieldRow>
          </Box>
          <Note>
            <strong>All three agree.</strong> They read the same cost layers, so the item detail, the
            Stock Value page, and the report&apos;s Closing Value reconcile to the same number for the
            same stock.
          </Note>
          <Note variant="warning">
            <strong>One exception.</strong> Cost layers hold what remains today, so Closing Value is
            exact for a period ending today. For a period that ended in the past, it reflects
            today&apos;s remaining batches. Opening Value is an estimate at the current cost — it
            isn&apos;t recalculated back to the period start.
          </Note>
        </Section>

        <Divider />

        {/* ── Recorded, estimated, and uncosted ──────────────────────────────── */}
        <Section icon={<BasisIcon fontSize="inherit" />} title="Recorded, estimated, and uncosted">
          <Box sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${theme.palette.divider}` }}>
            <FieldRow label="Recorded" accent>
              The cost was captured when the stock was received. Shown plainly, with no marker.
            </FieldRow>
            <FieldRow label="Estimated">
              A coloured dot marks a cost that wasn&apos;t recorded at purchase.{' '}
              <strong>Genesis</strong> — stock already on hand when cost tracking started, valued at
              its last known cost. <strong>Fallback</strong> — stock that ran short of a costed
              batch, valued at the last known cost. Hover the dot for the reason.
            </FieldRow>
            <FieldRow label="Uncosted">
              No price was ever recorded. Shown as <em>uncosted</em>; excluded from value totals and
              counted separately so it&apos;s never silently treated as zero.
            </FieldRow>
          </Box>
          <Note variant="warning">
            <strong>Fixing uncosted stock.</strong> Record the unit cost on the next receipt for that
            item, and new stock is costed from then on. To correct stock already on hand, an
            administrator can set the cost directly on the layer from the item&apos;s Cost section.
          </Note>
        </Section>

        <Divider />

        {/* ── Turning cost tracking on ───────────────────────────────────────── */}
        <Section icon={<ActivationIcon fontSize="inherit" />} title="Turning cost tracking on">
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            Activation values the stock you have on hand at its last known cost, as a starting point,
            and every movement after that records its own cost.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, mt: 1.25 }}>
            Figures dated before activation are estimates — the system doesn&apos;t invent history it
            never had. Reports that mix the two periods say from when values are recorded.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, mt: 1.25 }}>
            Activation doesn&apos;t change past records and can&apos;t be undone.
          </Typography>
        </Section>

        <Divider />

        {/* ── Corrections ────────────────────────────────────────────────────── */}
        <Section icon={<WarningIcon fontSize="inherit" />} title="Corrections">
          <Note variant="warning">
            <strong>Nothing is edited or deleted.</strong> A wrong cost is corrected by recording an
            adjustment against it — the original stays in the history, the correction sits alongside
            it, and the running value updates. Same principle as stock movements: corrections are
            appended, never overwritten, so the trail stays auditable.
          </Note>
        </Section>

        <Box sx={{ height: 8 }} />

      </DialogContent>

      {/* ── Sticky footer ───────────────────────────────────────────────────── */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          justifyContent: 'flex-end',
          background: isDark ? 'rgba(22,22,35,0.95)' : 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderRadius: '0 0 12px 12px',
        }}
      >
        <Typography
          variant="body2"
          color="text.disabled"
          sx={{ flex: 1, alignSelf: 'center', fontSize: '0.72rem' }}
        >
          Full documentation is available in the inventory operator manual.
        </Typography>
        <Box
          component="button"
          onClick={onClose}
          sx={{
            px: 3,
            py: 1,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: 'transparent',
            color: theme.palette.text.primary,
            fontWeight: 600,
            fontSize: '0.875rem',
            cursor: 'pointer',
            fontFamily: 'inherit',
            transition: 'all 0.15s',
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
              borderColor: theme.palette.text.secondary,
            },
          }}
        >
          Close
        </Box>
      </Box>
    </Dialog>
  );
};

export default CostTrackingHelpModal;
